import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Btn } from "@/components/kit";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "prox_news_seen";

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", consent: true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // eslint-disable-next-line -- one-time popup timer + open listener on mount
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("prox:newsletter", onOpen);
    let t;
    if (!localStorage.getItem(KEY)) {
      t = setTimeout(() => setOpen(true), 25000);
    }
    return () => { window.removeEventListener("prox:newsletter", onOpen); if (t) clearTimeout(t); };
  }, []);

  const dismiss = () => { setOpen(false); localStorage.setItem(KEY, "1"); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, f);
      track("newsletter_signup");
      localStorage.setItem(KEY, "1");
      toast.success("You are on the list. Check your inbox for a welcome note.");
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  if (!open) return null;
  const label = "t-caption font-mono uppercase tracking-wide text-[var(--muted)]";

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="newsletter-popup">
      <button aria-label="Close" className="absolute inset-0 bg-[#151412]/60" onClick={dismiss} data-testid="newsletter-overlay" />
      <div className="relative w-full max-w-sm max-h-[90vh] overflow-y-auto no-scrollbar bg-[var(--paper)] border border-[var(--stone)] rounded-[2px] p-6">
        <button aria-label="Close" onClick={dismiss} className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--ink)]" data-testid="newsletter-close"><X size={18} /></button>
        <p className="eyebrow">Field notes from Prox</p>
        <h3 className="t-h4 font-serif mt-2 pr-6">Sourcing and margin, in plain English.</h3>
        <p className="t-caption text-[var(--muted)] mt-2">Practical notes on product sourcing, supplier negotiation, costing and margin. A couple of times a month, no fluff.</p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label className={label} htmlFor="nl-name">Name</label>
            <input id="nl-name" className="field mt-1.5" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="newsletter-name" autoComplete="name" />
          </div>
          <div>
            <label className={label} htmlFor="nl-email">Email</label>
            <input id="nl-email" type="email" className="field mt-1.5" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="newsletter-email" autoComplete="email" />
          </div>
          <div>
            <label className={label} htmlFor="nl-phone">Phone (optional)</label>
            <input id="nl-phone" type="tel" className="field mt-1.5" placeholder="+61…" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="newsletter-phone" autoComplete="tel" />
          </div>
          <label className="flex items-start gap-2.5">
            <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[var(--clay)]" data-testid="newsletter-consent" />
            <span className="t-caption text-[var(--muted)]">Email me occasional notes. If I add a phone number, text me only about things I book.</span>
          </label>
          <Btn type="submit" variant="clay" className="w-full justify-center" data-testid="newsletter-submit">{busy ? "Joining…" : "Join the list"}</Btn>
        </form>
      </div>
    </div>
  );
}
